// `firebase/auth` stand-in over the API's /auth routes. Sessions persist in
// localStorage on the web or an AsyncStorage-like adapter on React Native.
import { FirebaseError, apiFetch, getApp, type FirebaseApp } from './app';

export interface Persistence { type: 'LOCAL' | 'SESSION' | 'NONE'; _store?: KV }
interface KV { getItem(k: string): Promise<string | null> | string | null; setItem(k: string, v: string): Promise<void> | void; removeItem(k: string): Promise<void> | void }

const memory = new Map<string, string>();
const memoryKV: KV = { getItem: (k) => memory.get(k) ?? null, setItem: (k, v) => { memory.set(k, v); }, removeItem: (k) => { memory.delete(k); } };
const webKV = (store: Storage): KV => ({
  getItem: (k) => { try { return store.getItem(k); } catch { return null; } },
  setItem: (k, v) => { try { store.setItem(k, v); } catch { /* private mode */ } },
  removeItem: (k) => { try { store.removeItem(k); } catch { /* ignore */ } },
});

export const inMemoryPersistence: Persistence = { type: 'NONE', _store: memoryKV };
export const browserLocalPersistence: Persistence = { type: 'LOCAL', _store: typeof localStorage !== 'undefined' ? webKV(localStorage) : memoryKV };
export const browserSessionPersistence: Persistence = { type: 'SESSION', _store: typeof sessionStorage !== 'undefined' ? webKV(sessionStorage) : memoryKV };
export const indexedDBLocalPersistence: Persistence = browserLocalPersistence;
export function getReactNativePersistence(storage: KV): Persistence { return { type: 'LOCAL', _store: storage }; }

interface Session { uid: string; email?: string; displayName?: string; emailVerified?: boolean; idToken: string; refreshToken: string; exp: number }

export interface IdTokenResult { token: string; claims: Record<string, any>; expirationTime: string; issuedAtTime: string; authTime: string; signInProvider: string | null; signInSecondFactor: null }

export class User {
  constructor(private readonly auth: Auth, private s: Session) {}
  get uid(): string { return this.s.uid; }
  get email(): string | null { return this.s.email ?? null; }
  get displayName(): string | null { return this.s.displayName ?? null; }
  get emailVerified(): boolean { return !!this.s.emailVerified; }
  get photoURL(): string | null { return (this.claims().picture as string) ?? null; }
  get phoneNumber(): string | null { return null; }
  get isAnonymous(): boolean { return false; }
  get providerData(): Array<{ providerId: string; uid: string; email: string | null }> { return [{ providerId: 'password', uid: this.s.email ?? this.s.uid, email: this.email }]; }
  get providerId(): string { return 'battlesuite'; }
  get metadata(): { creationTime?: string; lastSignInTime?: string } { return {}; }
  get refreshToken(): string { return this.s.refreshToken; }
  get tenantId(): null { return null; }
  /** @internal */ _session(): Session { return this.s; }
  /** @internal */ _update(s: Partial<Session>): void { this.s = { ...this.s, ...s }; }
  claims(): Record<string, any> { return decode(this.s.idToken); }
  async getIdToken(forceRefresh = false): Promise<string> { return this.auth._token(forceRefresh) as Promise<string>; }
  async getIdTokenResult(forceRefresh = false): Promise<IdTokenResult> {
    const token = await this.getIdToken(forceRefresh);
    const c = decode(token);
    return { token, claims: c, expirationTime: new Date((c.exp ?? 0) * 1000).toUTCString(), issuedAtTime: new Date((c.iat ?? 0) * 1000).toUTCString(), authTime: new Date((c.iat ?? 0) * 1000).toUTCString(), signInProvider: 'password', signInSecondFactor: null };
  }
  async reload(): Promise<void> { await this.auth._reload(); }
  async delete(): Promise<void> { throw new FirebaseError('auth/operation-not-allowed', 'Account deletion goes through support.'); }
  toJSON(): Record<string, unknown> { return { uid: this.uid, email: this.email, displayName: this.displayName, emailVerified: this.emailVerified }; }
}

function decode(jwt: string): Record<string, any> {
  try {
    const b = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const g = globalThis as any;
    const json: string = typeof atob === 'function' ? atob(b) : g.Buffer.from(b, 'base64').toString('utf8');
    return JSON.parse(decodeURIComponent(Array.from(json, (ch: string) => '%' + ch.charCodeAt(0).toString(16).padStart(2, '0')).join('')));
  } catch { return {}; }
}

export class Auth {
  currentUser: User | null = null;
  readonly config = { apiKey: '', authDomain: '' };
  languageCode: string | null = null;
  tenantId: string | null = null;
  settings = { appVerificationDisabledForTesting: false };
  private listeners = new Set<(u: User | null) => void>();
  private tokenListeners = new Set<(u: User | null) => void>();
  private ready: Promise<void>;
  private refreshing: Promise<string | null> | null = null;
  private store: KV;
  private readonly key: string;

  constructor(readonly app: FirebaseApp, persistence: Persistence = browserLocalPersistence) {
    this.store = persistence._store ?? memoryKV;
    this.key = `battlesuite:auth:${app._baseUrl}`;
    app._getToken = (force) => this._token(force);
    this.ready = this.restore();
  }

  private async restore(): Promise<void> {
    try {
      const raw = await this.store.getItem(this.key);
      if (raw) {
        const s = JSON.parse(raw) as Session;
        this.currentUser = new User(this, s);
        // NB: call _refresh directly, never _token — _token awaits `this.ready`,
        // which is the very promise restore() is resolving. Going through it here
        // deadlocks auth (and therefore every authenticated request) forever.
        if (s.exp * 1000 < Date.now() + 60_000) await this._refresh(true).catch(() => null);
      }
    } catch { /* corrupt session */ }
    this.emit();
  }

  async authStateReady(): Promise<void> { await this.ready; }
  async setPersistence(p: Persistence): Promise<void> {
    const raw = await this.store.getItem(this.key);
    await this.store.removeItem(this.key);
    this.store = p._store ?? memoryKV;
    if (raw) await this.store.setItem(this.key, raw);
  }
  useDeviceLanguage(): void { /* noop */ }

  private emit(): void {
    for (const l of this.listeners) { try { l(this.currentUser); } catch { /* listener */ } }
    for (const l of this.tokenListeners) { try { l(this.currentUser); } catch { /* listener */ } }
  }

  /** @internal */
  async _setSession(s: Session | null): Promise<User | null> {
    if (!s) { this.currentUser = null; await this.store.removeItem(this.key); this.emit(); return null; }
    this.currentUser = new User(this, s);
    await this.store.setItem(this.key, JSON.stringify(s));
    this.emit();
    return this.currentUser;
  }

  /** Why the last sign-out happened, kept beside the session so the login screen can say so
   *  ("refresh rejected: auth/invalid-refresh-token" vs "signed out from the app"). Best effort. */
  /** @internal */
  async _note(reason: string): Promise<void> {
    try { await this.store.setItem(`${this.key}:lastSignOut`, JSON.stringify({ at: Date.now(), reason })); } catch { /* best effort */ }
  }
  async lastSignOut(): Promise<{ at: number; reason: string } | null> {
    try { const raw = await this.store.getItem(`${this.key}:lastSignOut`); return raw ? JSON.parse(raw) as { at: number; reason: string } : null; } catch { return null; }
  }

  /** @internal */
  async _token(force = false): Promise<string | null> {
    await this.ready;
    return this._refresh(force);
  }

  /** Token read/refresh without waiting on `ready`, so restore() can use it. */
  private async _refresh(force = false): Promise<string | null> {
    const u = this.currentUser;
    if (!u) return null;
    const s = u._session();
    if (!force && s.exp * 1000 > Date.now() + 5 * 60_000) return s.idToken;
    if (!this.refreshing) {
      this.refreshing = (async () => {
        try {
          const r = await apiFetch(this.app, '/auth/token', { method: 'POST', auth: false, json: { refreshToken: s.refreshToken } });
          if (!r.ok) {
            // Sign out ONLY when the server says this refresh token itself is dead (revoked / expired).
            // Any other 401 — a proxy page, a half-deployed API, a body that failed to parse — used to
            // throw the phone back to the login screen while the token was still perfectly alive in
            // auth_refresh_tokens. Keep the session and let the next request retry.
            const code = r.status === 401 ? String((await r.json().catch(() => null))?.error?.code ?? '') : '';
            if (code === 'auth/invalid-refresh-token' || code === 'auth/user-disabled') {
              await this._note(`refresh rejected: ${code}`);
              await this._setSession(null); return null;
            }
            return s.idToken;   // transient failure: keep the old token
          }
          const j = await r.json() as { idToken: string; refreshToken: string };
          const next: Session = { ...s, idToken: j.idToken, refreshToken: j.refreshToken, exp: decode(j.idToken).exp ?? Math.floor(Date.now() / 1000) + 3600 };
          u._update(next);
          await this.store.setItem(this.key, JSON.stringify(next));
          for (const l of this.tokenListeners) { try { l(u); } catch { /* listener */ } }
          return next.idToken;
        } finally { this.refreshing = null; }
      })();
    }
    return this.refreshing;
  }

  /** @internal */
  async _reload(): Promise<void> {
    const u = this.currentUser; if (!u) return;
    const r = await apiFetch(this.app, '/auth/me');
    if (r.status === 401) { await this._note('profile reload rejected (401)'); await this._setSession(null); return; }
    if (!r.ok) return;
    const j = await r.json() as { email?: string; displayName?: string; emailVerified?: boolean };
    u._update({ email: j.email, displayName: j.displayName, emailVerified: j.emailVerified });
    await this.store.setItem(this.key, JSON.stringify(u._session()));
  }

  onAuthStateChanged(next: (u: User | null) => void, _err?: unknown, _done?: unknown): () => void {
    this.listeners.add(next);
    void this.ready.then(() => next(this.currentUser));
    return () => this.listeners.delete(next);
  }
  onIdTokenChanged(next: (u: User | null) => void): () => void {
    this.tokenListeners.add(next);
    void this.ready.then(() => next(this.currentUser));
    return () => this.tokenListeners.delete(next);
  }
  beforeAuthStateChanged(): () => void { return () => {}; }
  async signOut(): Promise<void> {
    const s = this.currentUser?._session();
    if (s) apiFetch(this.app, '/auth/signOut', { method: 'POST', auth: false, json: { refreshToken: s.refreshToken } }).catch(() => null);
    await this._note('signed out from the app');
    await this._setSession(null);
  }
  async updateCurrentUser(u: User | null): Promise<void> { await this._setSession(u ? u._session() : null); }
}

const auths = new WeakMap<FirebaseApp, Auth>();
export function getAuth(app: FirebaseApp = getApp()): Auth {
  let a = auths.get(app);
  if (!a) { a = new Auth(app); auths.set(app, a); }
  return a;
}
export function initializeAuth(app: FirebaseApp, deps?: { persistence?: Persistence | Persistence[] }): Auth {
  const p = Array.isArray(deps?.persistence) ? deps!.persistence[0] : deps?.persistence;
  const a = new Auth(app, p ?? browserLocalPersistence);
  auths.set(app, a);
  return a;
}
export function connectAuthEmulator(): void { /* noop */ }

// ---- helpers ---------------------------------------------------------------

async function authCall(auth: Auth, path: string, body: unknown, withAuth = false): Promise<any> {
  const r = await apiFetch(auth.app, path, { method: 'POST', auth: withAuth, json: body });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const code = j?.error?.code ?? (r.status === 401 ? 'auth/invalid-credential' : 'auth/internal-error');
    throw new FirebaseError(mapCode(code), j?.error?.message ?? `HTTP ${r.status}`);
  }
  return j;
}
// Our server codes mostly match Firebase's; a few client-side checks look for legacy names.
function mapCode(code: string): string {
  if (code === 'auth/invalid-credential') return 'auth/invalid-credential';
  if (code === 'auth/invalid-password') return 'auth/weak-password';
  return code;
}
function sessionFrom(j: any): Session {
  return { uid: j.localId ?? j.uid, email: j.email, displayName: j.displayName, emailVerified: j.emailVerified, idToken: j.idToken, refreshToken: j.refreshToken, exp: decode(j.idToken).exp ?? Math.floor(Date.now() / 1000) + 3600 };
}

export interface UserCredential { user: User; providerId: string | null; operationType: 'signIn' | 'link' | 'reauthenticate' }

export async function signInWithEmailAndPassword(auth: Auth, email: string, password: string): Promise<UserCredential> {
  const j = await authCall(auth, '/auth/signInWithPassword', { email, password });
  const user = (await auth._setSession(sessionFrom(j)))!;
  return { user, providerId: 'password', operationType: 'signIn' };
}
export async function createUserWithEmailAndPassword(auth: Auth, email: string, password: string): Promise<UserCredential> {
  const j = await authCall(auth, '/auth/signUp', { email, password });
  const user = (await auth._setSession(sessionFrom(j)))!;
  return { user, providerId: 'password', operationType: 'signIn' };
}
export async function signInWithCustomToken(auth: Auth, token: string): Promise<UserCredential> {
  const j = await authCall(auth, '/auth/signInWithCustomToken', { token });
  await auth._reload().catch(() => null);
  const user = (await auth._setSession({ ...sessionFrom(j), ...(auth.currentUser?._session() ?? {}) , idToken: j.idToken, refreshToken: j.refreshToken, uid: j.localId ?? j.uid }))!;
  await auth._reload();
  return { user, providerId: null, operationType: 'signIn' };
}
export async function signInWithApiKey(auth: Auth, key: string): Promise<UserCredential> {
  const j = await authCall(auth, '/auth/signInWithApiKey', { key });
  const user = (await auth._setSession(sessionFrom(j)))!;
  await auth._reload();
  return { user, providerId: null, operationType: 'signIn' };
}
export async function signOut(auth: Auth): Promise<void> { await auth.signOut(); }
/** Why the last sign-out happened on this device, if the SDK recorded one. */
export async function lastSignOut(auth: Auth = getAuth()): Promise<{ at: number; reason: string } | null> { return auth.lastSignOut(); }

export async function sendEmailVerification(user: User, _settings?: unknown): Promise<void> {
  await authCall(getAuth(), '/auth/sendEmailVerification', {}, true);
}
export async function sendPasswordResetEmail(auth: Auth, email: string, settings?: { url?: string }): Promise<void> {
  // The branded suite-wide callable. `settings.url` is where the reset page sends
  // the user afterwards — pass the calling product's own sign-in page.
  const r = await apiFetch(auth.app, '/fn/sendPasswordReset', { method: 'POST', auth: false, json: { data: { email, continueUrl: settings?.url } } });
  if (!r.ok) throw new FirebaseError('auth/internal-error', 'Could not send the reset email.');
}
export async function verifyPasswordResetCode(auth: Auth, code: string): Promise<string> {
  const j = await authCall(auth, '/auth/verifyResetCode', { oobCode: code });
  return j.email as string;
}
export async function confirmPasswordReset(auth: Auth, code: string, newPassword: string): Promise<void> {
  await authCall(auth, '/auth/confirmPasswordReset', { oobCode: code, newPassword });
}
export async function applyActionCode(auth: Auth, code: string): Promise<void> {
  await authCall(auth, '/auth/applyActionCode', { oobCode: code });
  await auth._reload().catch(() => null);
}
export async function checkActionCode(auth: Auth, code: string): Promise<{ data: { email: string | null }; operation: string }> {
  const j = await authCall(auth, '/auth/verifyResetCode', { oobCode: code });
  return { data: { email: j.email ?? null }, operation: 'PASSWORD_RESET' };
}
export async function updateProfile(user: User, p: { displayName?: string | null; photoURL?: string | null }): Promise<void> {
  const auth = getAuth();
  await authCall(auth, '/auth/updateProfile', p, true);
  user._update({ displayName: p.displayName ?? user.displayName ?? undefined });
  await auth._setSession(user._session());
}
export async function updatePassword(user: User, newPassword: string): Promise<void> {
  const auth = getAuth();
  const current = pendingReauth.get(user);
  if (!current) throw new FirebaseError('auth/requires-recent-login', 'Re-authenticate before changing the password.');
  const j = await authCall(auth, '/auth/changePassword', { currentPassword: current, newPassword }, true);
  pendingReauth.delete(user);
  await auth._setSession({ ...user._session(), idToken: j.idToken, refreshToken: j.refreshToken, exp: decode(j.idToken).exp });
}
export async function updateEmail(): Promise<void> { throw new FirebaseError('auth/operation-not-allowed', 'Email changes go through support.'); }

export interface AuthCredential { providerId: string; signInMethod: string; _email?: string; _password?: string }
export const EmailAuthProvider = {
  PROVIDER_ID: 'password',
  EMAIL_PASSWORD_SIGN_IN_METHOD: 'password',
  credential(email: string, password: string): AuthCredential { return { providerId: 'password', signInMethod: 'password', _email: email, _password: password }; },
};
const pendingReauth = new WeakMap<User, string>();
export async function reauthenticateWithCredential(user: User, cred: AuthCredential): Promise<UserCredential> {
  const auth = getAuth();
  const j = await authCall(auth, '/auth/signInWithPassword', { email: cred._email, password: cred._password });
  if ((j.localId ?? j.uid) !== user.uid) throw new FirebaseError('auth/user-mismatch', 'Credentials belong to a different user.');
  pendingReauth.set(user, cred._password!);
  await auth._setSession({ ...user._session(), idToken: j.idToken, refreshToken: j.refreshToken, exp: decode(j.idToken).exp });
  return { user: auth.currentUser!, providerId: 'password', operationType: 'reauthenticate' };
}
export function onAuthStateChanged(auth: Auth, next: (u: User | null) => void, err?: unknown, done?: unknown): () => void { return auth.onAuthStateChanged(next, err, done); }
export function onIdTokenChanged(auth: Auth, next: (u: User | null) => void): () => void { return auth.onIdTokenChanged(next); }
export async function getIdToken(user: User, force?: boolean): Promise<string> { return user.getIdToken(force); }
export async function getIdTokenResult(user: User, force?: boolean): Promise<IdTokenResult> { return user.getIdTokenResult(force); }
export async function reload(user: User): Promise<void> { return user.reload(); }
export async function setPersistence(auth: Auth, p: Persistence): Promise<void> { return auth.setPersistence(p); }
export type { Persistence as AuthPersistence };
